import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Sparkles, X, MessageCircle, CalendarCheck2, Hourglass, Bell, ShieldCheck, Bug } from "lucide-react";

const SUPPORT_WHATSAPP = "390832364051"; // wa.me requires international format without +
const SUPPORT_EMAIL = "info@assiteclecce.it";

/**
 * BetaWelcomeModal — shown once to a business owner on first login.
 * Persisted server-side (db.users.beta_welcome_seen_at) so it doesn't reappear
 * on a different device. The user can dismiss it; clicking "Inizia" or pressing
 * Esc closes it and marks it seen.
 *
 * Mounted in BusinessDashboard. Only shows when the user is loaded and
 * /my/beta-welcome-status returns seen=false.
 */
export default function BetaWelcomeModal() {
  const [show, setShow] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      try {
        const me = await api.get("/auth/me");
        const status = await api.get("/my/beta-welcome-status");
        if (cancelled) return;
        setUser(me.data);
        if (!status.data?.seen && (me.data?.role === "business" || me.data?.role === "admin")) {
          setShow(true);
        }
      } catch (e) {
        // silent — if /auth/me fails, the protected route would have redirected anyway
      }
    }
    check();
    return () => { cancelled = true; };
  }, []);

  async function dismiss() {
    setShow(false);
    try { await api.post("/my/beta-welcome-seen"); } catch (e) { /* best-effort */ }
  }

  if (!show) return null;

  const features = [
    { icon: CalendarCheck2, title: "Prenotazioni in 3 tap", desc: "Il link pubblico è già pronto: gli orari liberi vengono calcolati al volo." },
    { icon: Hourglass, title: "Lista d'attesa attiva", desc: "I clienti in attesa vengono avvisati automaticamente quando si libera uno slot." },
    { icon: Bell, title: "Notifiche istantanee", desc: "Push su qualsiasi dispositivo (anche senza email Resend configurata)." },
    { icon: ShieldCheck, title: "GDPR-ready", desc: "Privacy, consenso, retention 10 anni e diritto all'oblio già implementati." },
  ];

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4 overflow-y-auto" data-testid="beta-welcome-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl my-auto dark:bg-zinc-900">
        {/* Hero */}
        <div className="relative rounded-t-3xl bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-500 px-8 pt-8 pb-12 text-white overflow-hidden">
          <button onClick={dismiss} className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="beta-welcome-close">
            <X className="h-4 w-4" />
          </button>
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase bg-white/15 rounded-full px-3 py-1.5">
            <Sparkles className="h-3 w-3" /> Beta program
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-4 leading-tight">
            Benvenuto/a, {user?.name?.split(" ")[0] || "tester"}.<br />
            <span className="text-white/80">Sei tra i primi a provare Prenotoo.</span>
          </h2>
          <p className="text-white/90 text-sm mt-3 max-w-md">
            Stiamo lanciando la beta con un gruppo selezionato di attività. Il tuo feedback farà la differenza.
          </p>
        </div>

        {/* Body */}
        <div className="px-8 py-7 space-y-6">
          <div>
            <h3 className="font-display text-lg font-medium mb-3">Cosa puoi fare subito</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {features.map((f, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-2xl bg-zinc-50 border border-zinc-100 dark:bg-zinc-800 dark:border-zinc-800">
                  <div className="h-9 w-9 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 dark:bg-blue-950/40">
                    <f.icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{f.title}</div>
                    <div className="text-[12px] text-zinc-600 leading-snug mt-0.5 dark:text-zinc-400">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800/50 dark:bg-emerald-950/40">
            <div className="text-xs font-semibold tracking-[0.15em] uppercase text-emerald-800 inline-flex items-center gap-1.5 dark:text-emerald-300">
              <MessageCircle className="h-3.5 w-3.5" /> Supporto diretto
            </div>
            <p className="text-sm text-emerald-900 mt-1.5 leading-relaxed dark:text-emerald-200">
              Hai una domanda o vuoi parlare con noi? Scrivici su <strong>WhatsApp 0832 364051</strong> o via email a <strong>{SUPPORT_EMAIL}</strong>. Risposta entro 24h lavorative.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent('Ciao, ho una domanda su Prenotoo')}`}
                target="_blank"
                rel="noreferrer"
                className="h-9 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1.5"
                data-testid="beta-welcome-whatsapp"
              >
                <MessageCircle className="h-3.5 w-3.5" /> Apri WhatsApp
              </a>
              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Supporto Prenotoo Beta')}`}
                className="h-9 px-3 rounded-full bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-100 text-xs font-semibold inline-flex items-center gap-1.5 dark:bg-zinc-900 dark:text-emerald-300"
                data-testid="beta-welcome-email"
              >
                Scrivi una email
              </a>
            </div>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/50 dark:bg-amber-950/40">
            <div className="text-xs font-semibold tracking-[0.15em] uppercase text-amber-900 inline-flex items-center gap-1.5 dark:text-amber-200">
              <Bug className="h-3.5 w-3.5" /> Trovi un bug?
            </div>
            <p className="text-sm text-amber-900 mt-1.5 dark:text-amber-200">
              In basso a destra c'è un bottone <strong>"Segnala bug"</strong> sempre visibile. Apre un form: titolo, descrizione, screenshot automatico. Lo riceviamo immediatamente e ti aggiorniamo quando è risolto.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button onClick={dismiss} className="btn-accent flex-1 h-12" data-testid="beta-welcome-start">
              Inizia ad usare Prenotoo
            </button>
          </div>

          <p className="text-[10px] text-zinc-400 text-center dark:text-zinc-500">
            Questo messaggio non verrà mostrato di nuovo. Puoi sempre contattarci dai contatti in alto.
          </p>
        </div>
      </div>
    </div>
  );
}
